import React, {useEffect, useState} from "react"
import { Confirm, useNotify } from "react-admin"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core"

import createPhoneCall from "./createPhoneCall"
import getHttpsCallable from "../../../utils/getHttpsCallable"
import {find} from "lodash"
import { PhoneCallResult } from "../../../lib/enums"
import { db } from "../../../firebase"

const sendEmailTemplate = getHttpsCallable("admin-sendEmailTemplate")
const sendWhatsAppTemplate = getHttpsCallable("admin-sendWhatsappTemplate")
const getTemplates = getHttpsCallable("admin-getTemplates")

interface CallAcceptedDialogParams {
  open: boolean
  handleClose: (ticketRef?: any) => void
  emailTemplates: Array<any>
  ticket: Ticket
}

export default function CallAcceptedDialog (
  { open, handleClose, emailTemplates, ticket }: CallAcceptedDialogParams,
) {
  const notify = useNotify()
  const [notes, setNotes] = useState("")
  const [whatsAppTemplates, setWhatsAppTemplates] = useState([])
  const [template, setTemplate] = useState("")
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [fetchData, setFetchData] = useState(true)
  const [cancelingCase, setCancelingCase] = useState<boolean>(false)
  const [claimData, setClaimData] = useState<null|Partial<Claim>>(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailSendResult, setEmailSendResult] =
    useState<SendEmailTemplateResult | null>(null)
  const [sendingWhatsApp, setSendingWhatsApp] =
    useState(false)
  const [WhatsAppSendResult, setWhatsAppSendResult] =
    useState<{sent: boolean} | null>(null)
  const [cancelCaseDialog, setCancelCaseDialog] =
    useState<boolean>(false)
  const [claimActionStatus, setClaimActionStatus] =
    useState<string | undefined>(undefined)

  useEffect(() => {
    async function getClaimActionStatus () {
      const claims = await db
        .collectionGroup("claims")
        .where("id", "==", ticket.claimId)
        .get()

      if (!claims.size || !claims.docs[0]) {
        console.error(`Claim ${ticket.claimId} not found`)
      }

      const claim = claims.docs[0].data() as Partial<Claim>

      if (claim) {
        setClaimData(claim)
      }

      setClaimActionStatus(claim.claimActionStatus)
    }

    if (fetchData) {
      getClaimActionStatus()
      setFetchData(false)
    }
  }, [fetchData])

  return (
    <Dialog open={open}>
      <DialogContent>
        <DialogContentText>
          Leave any notes here about the phone call
          or do any of the following actions.
        </DialogContentText>
        <>
          <FormControl style={{minWidth: 300, display: "flex"}}>
            <InputLabel htmlFor='template'>Template</InputLabel>
            <Select
              error={Boolean(templateError)}
              disabled={sendingEmail}
              value={template}
              onChange={(event: any /* ChangeEvent */) => {
                console.info("template selected", event.target.value)
                setTemplate(event.target.value)
                setTemplateError(null)
              }}
            >
              {Object
                .values(emailTemplates)
                .sort((a: Template, b: Template) : number =>
                  (a.userDefinedName && b.userDefinedName)
                    ? a.userDefinedName.localeCompare(b.userDefinedName)
                    : 0,
                )
                .sort((a: Template, b: Template) : number =>
                  Number(a.userDefinedName?.includes(":")) -
                  Number(b.userDefinedName?.includes(":")),
                )
                .map((template: Template) =>
                  (<MenuItem key={template.name} value={template.name}>
                    {template.userDefinedName}
                  </MenuItem>))
              }
            </Select>
            {templateError &&
              <FormHelperText>{templateError}</FormHelperText>
            }
          </FormControl>
          <Button
            fullWidth
            color='primary'
            variant='outlined'
            disabled={
              Boolean(!template || sendingEmail || emailSendResult?.sent)}
            style={{marginBottom: "15px", marginTop: "1rem"}}
            onClick={async () => {
              if (emailSendResult) {
                return
              }

              setSendingEmail(true)

              const selectedTemplate: any = find(
                emailTemplates,
                (t: Template) =>
                  t.userDefinedName === template,
              )

              if (!selectedTemplate) {
                notify("Error: template not found", "warning")
                setSendingEmail(false)
                return
              }

              const result = await sendEmailTemplate({
                claimIds: [ticket.claimId],
                templateName: selectedTemplate?.name,
              })
              setEmailSendResult(result.data[0])
              setSendingEmail(false)
            }}
          >
            {!emailSendResult ? (sendingEmail
              ? <CircularProgress size={20} />
              : `Send ${template ? "\"" + template + "\"" : ""} Email`)
              : (emailSendResult?.sent
                ? `"${template}" Email Sent`
                : `"${template}" Email Failed to Send`)
            }
          </Button> 
        </>
      </DialogContent>
      <DialogContent>
        <TextField
          autoFocus
          margin='dense'
          id='name'
          label='Call Notes'
          fullWidth
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={() => handleClose()}>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={async () => {
            try {
              await createPhoneCall({
                claimId: ticket.claimId,
                callResult: PhoneCallResult.ANSWERED,
                phoneNumberValid: true,
                phoneNumber: ticket.claimPhoneNumber,
                callNotes: notes,
              })
              handleClose()
            }
            catch (error) {
              notify(`${error}`, "warning")
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
